import { api } from './api';
import {SubscriptionPlan} from "../types/SubscriptionPlan";

export interface BulkActivatePayload {
    city: string;
    planId: number;
    appType: 'JustZapIt' | 'ApnaYatri';
    testPhoneNumber?: string;
}

export const bulkActivateDrivers = (payload: BulkActivatePayload) => {
    return api.post('/admin/bulk-activate', payload);
};

export const getSubscriptionPlans = () =>
    api.get<SubscriptionPlan[]>('/admin/subscription-plans');

// ===== Launch Mode (per-city guaranteed-reach + free-plan accept bypass) =====

export interface LaunchModeResponse {
    success: boolean;
    cities: string[];
    message?: string;
}

export const getLaunchMode = () =>
    api.get<LaunchModeResponse>('/admin/ops/launch-mode');

export const setLaunchMode = (city: string, enabled: boolean) =>
    api.post<LaunchModeResponse>('/admin/ops/launch-mode', { city, enabled });

export interface ForceOnlinePayload {
    city: string;
    appType: 'JustZapIt' | 'ApnaYatri';
    testPhoneNumber?: string;
}

export interface ForceOnlineResponse {
    success: boolean;
    message: string;
    forcedCount: number;
    tokensPushed: number;
}

export const forceOnlineDrivers = (payload: ForceOnlinePayload) =>
    api.post<ForceOnlineResponse>('/admin/ops/force-online', payload);

// ===== Dispatch Insights (per-ride reach report) =====

export interface ReachedDriver {
    uid: string;
    name: string | null;
    phone: string | null;
    tier: 'live' | 'woken';
    status: string;
    distanceKm: number | null;
    reaction?: 'accepted' | 'rejected' | 'none';
}

export interface DemandNudgeReport {
    fired: boolean;
    candidates: number;
    sent: number;
}

// Offline-wake breakdown (§23) — what happened + what WOULD have happened (quiet / recency / cap / reject).
export interface OfflineWakeReport {
    mode: 'gentle' | 'loud';
    nearTotal: number;
    recencyExcluded: number;
    quietHoursActive: boolean;
    candidates: number;
    suppressedReject: number;
    suppressedCap: number;
    wouldNotify: number;
    notified: number;
    recencyMin?: number;    // present in the preview projection
    staleTotal?: number;    // TIER 2.5: near-but-stale drivers (gentle ride-context nudge)
    staleNotified?: number; // TIER 2.5: of those, actually nudged (actuals only)
}

// Post-dispatch lifecycle event (A2): waves / probe rechecks / handoffs, oldest first.
export interface DispatchTimelineEvent {
    t: number;                 // epoch ms
    event: 'wave' | 'probe_sent' | 'probe_recheck' | 'handoff' | string;
    // wave: {wave, radiusKm, live, offline, fcmOk} · probe_sent: {count, recheckMs}
    // probe_recheck: {probed, nowFresh, rung, fcmOk?} · handoff: {driver, fcmOk}
    [k: string]: string | number | boolean | undefined;
}

export interface DispatchReport {
    rideId: number;
    cityId: string | null;
    type: string | null;
    reached: number;
    drivers?: ReachedDriver[];
    liveConnected: number;
    wokenViaFcm: number;
    status: { online: number; silent: number; offline: number };
    fcm: { sent: number; accepted: number; failed: number };
    accepted: { count: number; by: string | null; byName?: string | null; byPhone?: string | null; at: number | null };
    rejected: number;
    ignored: number;
    demandNudge?: DemandNudgeReport;
    offlineWake?: OfflineWakeReport | null;
    createdAt: number;
    events?: DispatchTimelineEvent[]; // A2 timeline (waves / probes / handoffs)
}

export const listDispatchReports = (cityId?: string, limit = 50) =>
    api.get<{ success: boolean; reports: DispatchReport[] }>('/admin/ops/dispatch-reports', {
        params: { ...(cityId ? { cityId } : {}), limit },
    });

export const getDispatchReport = (rideId: number) =>
    api.get<{ success: boolean; report: DispatchReport }>(`/admin/ops/dispatch-report/${rideId}`);

// ===== Reach Preview (predictive — who a ride HERE would reach, sends nothing) =====

export interface DispatchPreview {
    cityId: string;
    launchModeOn: boolean;
    tier1: { radiusKm: number; count: number };
    tier2: { enabled: boolean; radiusKm: number; maxResults: number; count: number };
    tier3?: { enabled: boolean; note: string; count: number };
    demandNudge?: {
        enabled: boolean;
        launchModeOn: boolean;
        minNearReach: number;
        nearReach: number;
        wouldFire: boolean;
        audience: number;
        note: string;
        quietHoursActive?: boolean;
    };
    offlineWake?: OfflineWakeReport;
    totalReach: number;
    drivers: ReachedDriver[];
}

export const getDispatchPreview = (lat: number, lng: number, type?: string) =>
    api.get<{ success: boolean } & DispatchPreview>('/admin/ops/dispatch-preview', {
        params: { lat, lng, ...(type ? { type } : {}) },
    });

// ===== Supply Funnel (#7) — why "registered" collapses to "reachable" =====

// A3 — config-lint finding (known-bad / notable reach-config combinations).
export interface LintFinding {
    severity: 'warn' | 'info';
    keys: string[];
    message: string;
}

export interface SupplyFunnel {
    cityId: string;
    funnel: {
        registered: number;
        hasToken: number;
        tokenFresh30d: number;
        locFresh7d: number;
        locFresh24h: number;
        acceptedThisWeek: number;
    };
    segments: { tokenNull: number; dormant7d: number };
    // B4: week-over-week deltas vs the newest daily snapshot ≥7 days old (null until history exists).
    trend?: { since: string; deltas: Record<string, number> } | null;
    // A3: config lint findings (warnings first).
    lint?: LintFinding[];
    note: string;
    // Snapshot-first, like adoption: these are fleet-composition metrics, so the default read serves
    // last night's precomputed row instead of six live scans over drivers-joined-users.
    source?: 'snapshot' | 'live';
    asOf?: string;
}

export const getSupplyFunnel = (city: string, fresh = false) =>
    api.get<{ success: boolean } & SupplyFunnel>('/admin/ops/supply-funnel', {
        params: { city, ...(fresh ? { fresh: 1 } : {}) },
    });

// ===== Supply-gap zones (#11 + B5 drilldown) — geo cells where rides chronically fail =====

export interface SupplyGapZone {
    lat: number;
    lng: number;
    total: number;
    failed: number;
    expired: number;            // B5: failure mode — expired unaccepted
    lowReach: number;           // B5: failure mode — reached < minNearReach
    peakHours: number[];        // B5: top-3 IST hours failures cluster in
    staleSupplyNearby: number;  // B5: wake-able (probe/sweep-band) drivers in the cell; 0 = desert; -1 = not computed (tail cell)
}

export interface SupplyGapZones {
    cityId: string;
    days: number;
    minNearReach: number;
    totalRides: number;
    zones: SupplyGapZone[];
}

export const getSupplyGapZones = (city: string, days = 14, minNearReach = 10) =>
    api.get<{ success: boolean } & SupplyGapZones>('/admin/ops/supply-gap', {
        params: { city, days, minNearReach },
    });

// ===== A1 — Driver Reachability Inspector: "why isn't this driver getting rides?" =====

export interface DriverReachability {
    user: { uid: string; name: string | null; phone: string | null; suspended: boolean; currentCity: string | null; homeCity: string | null };
    driver: {
        status: string; isDeleted: boolean; isSuspended: boolean; vehicleType: string | null;
        lastCityId: string | null; lastLat: number | null; lastLng: number | null;
        lastLocationAt: string | null; locAgeSec: number | null;
    } | null;
    token: { present: boolean; updatedAt: string | null; ageHours: number | null };
    presence: {
        redisStatus: string | null; redisStatusTtlSec: number | null;
        onDuty: boolean; onDutyTtlSec: number | null;
        currentCity: string | null;
        geo: { lat: number; lng: number } | null;
        geoVsDurableKm: number | null;
        vehicleRedis: string | null;
    };
    cooldowns: {
        offlineWakeCount: number; offlineWakeCountTtlSec: number | null;
        offlineWakeBlocked: boolean; offlineWakeBlockTtlSec: number | null;
        burstWakeTtlSec: number | null;
        demandNudgeTtlSec: number | null;
        locPingCount: number; locPingTtlSec: number | null;
        pendingHandoffRideId: number | null; pendingHandoffTtlSec: number | null;
    };
    streak: { count: number; lastDate: string | null };
    flags: string[];
}

export const getDriverReachability = (q: string) =>
    api.get<{ success: boolean } & DriverReachability>('/admin/ops/driver-reachability', { params: { q } });

// ===== B6 — Retrospective reach explainer: which gate dropped driver X from ride Y =====

export interface ReachGate { gate: string; pass: boolean; detail: string; }

export interface ReachExplanation {
    rideId: number;
    ride: { status: string; type: string | null; requestedAt: string; acceptedBy: string | null; pickupCity: string | null };
    driver: { uid: string; name: string | null; phone: string | null; distKm: number | null };
    gates: ReachGate[];
    verdict: string;
    caveats: string[];
}

export const explainReach = (rideId: number, q: string) =>
    api.get<{ success: boolean } & ReachExplanation>('/admin/ops/explain-reach', { params: { rideId, q } });

// ===== Historical Dispatch (durable DispatchLog) — per-ride reach beyond the live 6h Redis window =====

// Compact durable row — NO per-driver reactions (those lived only in the expired Redis hash).
export interface DispatchLogRow {
    rideId: number;
    cityId: string | null;
    reached: number;
    tier1Count: number;
    tier2Count: number;
    recencyExcluded: number;
    staleTotal: number;
    nudgeFired: boolean;
    outcome: string;              // pending | accepted | expired | cancelled
    createdAt: string;
    resolvedAt: string | null;
}

export interface DispatchHistory {
    rows: DispatchLogRow[];
    summary: Record<string, number>; // outcome → count across the WHOLE filter
    total: number;
    limit: number;
    offset: number;
}

export interface DispatchHistoryParams {
    cityId?: string;
    from?: string;
    to?: string;
    outcome?: string;
    limit?: number;
    offset?: number;
}

export const getDispatchHistory = (params: DispatchHistoryParams) =>
    api.get<{ success: boolean } & DispatchHistory>('/admin/ops/dispatch-history', { params });
// ===== App-version adoption (#5 location_ping rollout gate) =====
// "How much of the fleet can actually service a silent location_ping yet?" Drives the decision of
// when to raise LOCATION_PING_MIN_APP_VERSION, and supplies the distinct-version list used by the
// App version filter on the driver/user lists.

export interface AppVersionAdoption {
    cityId: string | null;
    role: 'driver' | 'rider' | 'all';
    // The location_ping gate is driver-only; for role=rider these numbers are reporting, not a gate.
    gateAppliesToRole: boolean;
    minAppVersion: number;      // 0 = gate off
    gateActive: boolean;
    totalReachable: number;     // drivers holding an FCM token — the population the gate applies to
    tokenless: number;          // unreachable by push entirely (separate problem)
    eligible: number;
    eligiblePct: number;
    unknown: number;            // appVersion NULL — old build, or not reopened since updating
    versions: { version: number | null; count: number }[]; // newest first, null last

    // Adoption diagnosis: is a large `unknown` a stalling rollout or just a dormant fleet?
    activeDays: number;
    unknownActive: number;      // old build AND opened the app recently -> rollout is stalling
    unknownDormant: number;     // old build and gone quiet -> will not update, stop waiting
    knownActive: number;
    stallingPct: number;        // of recently-seen users, the share still on an old build

    // Where these numbers came from. 'snapshot' = the nightly precomputed row (an indexed lookup, so
    // safe to call from every list mount). 'live' = a full aggregate over users, only on an explicit
    // recompute or the very first read before a snapshot exists.
    source: 'snapshot' | 'live';
    asOf: string | null;        // IST day the numbers represent (YYYY-MM-DD)
    // Week-over-week movement. stallingPct is the one that should FALL as the rollout works;
    // eligiblePct crawls because dormant accounts dilute it. null until a week of history exists.
    trend?: { since: string; eligiblePct: number; stallingPct: number } | null;
    // Chunked-scan telemetry. `truncated` means the walk hit its safety ceiling, so every count is an
    // UNDERCOUNT — the panel must say so rather than let it read as a real drop in adoption.
    scan?: { chunks: number; rows: number; truncated: boolean };
}

/** `fresh` forces a live recompute (expensive); omit it for the cheap snapshot read. */
export const getAppVersionAdoption = (
    city?: string,
    role: 'driver' | 'rider' | 'all' = 'driver',
    fresh = false,
) =>
    api.get<{ success: boolean } & AppVersionAdoption>('/admin/ops/app-adoption', {
        params: { ...(city ? { city } : {}), role, ...(fresh ? { fresh: 1 } : {}) },
    });
